import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { Preview } from '@renderer/components'
import Button from '@renderer/components/Button'
import { BackgroundContainer } from '@renderer/components/BackgroundContainer'
import { useConfigStore } from '@renderer/stores/configState'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import { useDesignLayout } from '@renderer/hooks'
import { useBackgroundTextColor } from '@renderer/hooks/useBackgroundTextColor'

const CameraPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState } = useSessionStateStore()
  const { selectedSecondaryCamera } = useSettingsStore()
  const { config } = useConfigStore()
  const { primaryColor } = config?.theme || { primaryColor: '#3B82F6' }
  const { isLandscape } = useDesignLayout()
  const textColor = useBackgroundTextColor()

  const handleCameraSelect = (camera: 'primary' | 'secondary'): void => {
    setSessionState({
      userPreferences: {
        selectedCamera: camera
      },
      timeline: {
        activityLogs: [
          {
            page: 'camera',
            type: 'ui',
            action: 'camera_selected',
            details: { camera },
            timestamp: new Date()
          }
        ]
      }
    })
    
    // Determine next page. Welcome sends every secondary-camera session here first,
    // before whatever it would otherwise have navigated to — so this page owes the
    // flow that deferred destination. When startWithPayment is on, that destination
    // is checkout; forwarding straight to filter/frame instead drops the money step
    // for the rest of the session and the booth captures for free.
    const welcomeSettings = config?.pages?.welcome?.settings
    const filterSettings = config?.pages?.filter?.settings
    const startWithPayment = welcomeSettings?.startWithPayment || false
    const applyAfterCapture = filterSettings?.applyAfterCapture || false
    const frameEnabled = config?.pages?.frame?.settings?.enabled || false

    let nextPage: string
    if (startWithPayment) {
      // Checkout → payment continues the flow, and applies applyAfterCapture
      // itself once the transaction settles.
      nextPage = 'checkout'
    } else if (applyAfterCapture) {
      // Filter runs AFTER capture, so head straight for the capture leg —
      // same choice Welcome makes. Routing to filter here would show it first
      // and contradict the setting.
      nextPage = frameEnabled ? 'frame' : 'capture'
    } else if (filterSettings?.enabled) {
      // Filter runs before capture
      nextPage = 'filter'
    } else {
      // Filter disabled: go to frame
      nextPage = 'frame'
    }

    stateUploader.uploadSessionState()
    navigate(`/${projectId}/${nextPage}`)
  }

  return (
    <BackgroundContainer className="flex flex-col items-center justify-center gap-6 p-8 overflow-hidden">
      <h1 className="text-4xl font-bold shrink-0" style={{ color: textColor }}>
        {t('camera.title', 'Choose Your Camera')}
      </h1>
      {/*
        Lay the camera panes out by ORIENTATION, not by `shortSide`. A landscape screen's
        short side is its (small) height, so gating two columns on `shortSide >= 768` wrongly
        collapsed wide screens to a single column — the panes stacked and overflowed off-screen.
        Row in landscape / column in portrait, with each pane bounded by the available height
        (flex-1 + min-h-0) so the fixed 16:9 box can no longer push content past the viewport.
      */}
      <div
        className={`flex w-full max-w-6xl flex-1 min-h-0 justify-center gap-8 ${
          isLandscape ? 'flex-row' : 'flex-col'
        }`}
      >
        {/* Primary Camera */}
        <div className="flex flex-1 min-h-0 min-w-0 flex-col items-center">
          <div className="w-full flex-1 min-h-0 bg-black mb-4 rounded-lg overflow-hidden">
            <Preview cameraKey="primary" />
          </div>
          <Button
            text={t('camera.primary', 'Primary Camera')}
            onClick={() => handleCameraSelect('primary')}
            style={{ backgroundColor: primaryColor }}
            className="!text-white !font-bold !py-3 !px-8 !text-xl shrink-0"
          />
        </div>

        {/* Secondary Camera */}
        {selectedSecondaryCamera && (
          <div className="flex flex-1 min-h-0 min-w-0 flex-col items-center">
            <div className="w-full flex-1 min-h-0 bg-black mb-4 rounded-lg overflow-hidden">
              <Preview cameraKey="secondary" />
            </div>
            <Button
              text={t('camera.secondary', 'Secondary Camera')}
              onClick={() => handleCameraSelect('secondary')}
              style={{ backgroundColor: primaryColor }}
              className="!text-white !font-bold !py-3 !px-8 !text-xl shrink-0"
            />
          </div>
        )}
      </div>
    </BackgroundContainer>
  )
}

export default CameraPage
